import { QuestionType } from '@/API';
import AnimatedFeelingAfter from '@/components/Animated/Backgrounds/AnimatedFeelingAfterBackground';
import { useSession, useUpdatePlayerQuestion } from '@/hooks/PlayerSessionControlHooks';
import { faSpinner, faThumbsDown, faThumbsUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { useState } from 'react';

enum Sentiment {
  positive,
  negative
}

interface SentimentMeterProps {
  selectedSentiment: Sentiment | null;
  setSelectedSentiment: (sentiment: Sentiment) => void;
}

const SentimentMeter = ({ selectedSentiment, setSelectedSentiment }: SentimentMeterProps) => {
  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <div className="feeling-container flex justify-between items-center w-full rounded-3xl p-8 bg-slate-100 shadow-lg">
        <div
          className={clsx(
            'p-4 m-4 cursor-pointer',
            'transition-all duration-300',
            'flex justify-center items-center w-full space-x-48'
          )}
        >
          <button onClick={() => setSelectedSentiment(Sentiment.negative)}>
            <FontAwesomeIcon
              icon={faThumbsDown}
              className={clsx(
                'text-[#f29b3d] text-3xl p-6 -m-8 transition-all duration-300',
                selectedSentiment === Sentiment.negative && 'bg-blue-200 rounded-[20px]'
              )}
            />
          </button>
          <button onClick={() => setSelectedSentiment(Sentiment.positive)}>
            <FontAwesomeIcon
              icon={faThumbsUp}
              className={clsx(
                'text-[#f29b3d] text-3xl p-6 -m-8 transition-all duration-300',
                selectedSentiment === Sentiment.positive && 'bg-green-200 rounded-[20px]'
              )}
            />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const questions = {
  [QuestionType.CREATIVITY]: {
    element: (
      <>
        Do you feel more <span className="font-bold">creative</span>?
      </>
    ),
    text: 'Do you feel more creative?'
  },
  [QuestionType.PARTICIPATION]: {
    element: (
      <>
        Did you enjoy <span className="font-bold">participating?</span>
      </>
    ),
    text: 'Did you enjoy participating?'
  },
  [QuestionType.CONNECTED]: {
    element: (
      <>
        Do you feel more <span className="font-bold">connected?</span>
      </>
    ),
    text: 'Do you feel more connected'
  }
};

const PlayerQuestion = () => {
  const [selectedSentiment, setSelectedSentiment] = useState<Sentiment | null>(null);
  const [loading, setLoading] = useState(false);
  const session = useSession();
  const updateQuestion = useUpdatePlayerQuestion();

  const submit = async () => {
    if (!session?.questionType || selectedSentiment === null) {
      return;
    }

    setLoading(true);
    await updateQuestion(
      questions[session.questionType].text,
      selectedSentiment === Sentiment.positive
    );
    setLoading(false);
  };

  return (
    <>
      <AnimatedFeelingAfter />
      {session?.questionType && (
        <div className="max-w-288">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.h1
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-[#20145F] font-normal text-4xl mb-24 text-center"
            >
              {questions[session.questionType].element}
            </motion.h1>
          </motion.div>
          <SentimentMeter
            selectedSentiment={selectedSentiment}
            setSelectedSentiment={setSelectedSentiment}
          />
          <button
            className="btn btn-primary btn-lg w-full mt-16"
            onClick={submit}
            disabled={loading || selectedSentiment === null}
          >
            Next
            {loading && <FontAwesomeIcon icon={faSpinner} className="ml-8" spin />}
          </button>
        </div>
      )}
    </>
  );
};

export default PlayerQuestion;
